import { useMemo, useState, useEffect } from "react";
import { Sparkle, WarningCircle, CircleNotch } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { useEditorStore } from "../../stores/editor";
import { useVitalsStore } from "../../stores/vitals";
import { useCompletionStore } from "../../stores/completion";
import { onGhostTextStatus, type GhostTextStatus } from "./ghost-text";

interface StatusBarProps {
  content: string;
  docType: "latex" | "markdown" | "docx";
  className?: string;
}

function countWords(text: string, docType: "latex" | "markdown" | "docx"): number {
  let stripped = text;
  if (docType === "latex") {
    stripped = text
      .replace(/%.*$/gm, "")
      .replace(/\\[a-zA-Z]+(\{[^}]*\})*/g, " ")
      .replace(/[{}[\]$\\]/g, " ");
  } else if (docType === "docx") {
    stripped = text.replace(/<[^>]*>/g, " ");
  }
  return stripped.split(/\s+/).filter((w) => w.length > 0 && /[a-zA-Z0-9]/.test(w)).length;
}

function CompletionIndicator() {
  const enabled = useCompletionStore((s) => s.enabled);
  const toggle = useCompletionStore((s) => s.setEnabled);
  const [ghostStatus, setGhostStatus] = useState<GhostTextStatus>("idle");
  const [ghostError, setGhostError] = useState<string | undefined>();

  useEffect(() => {
    return onGhostTextStatus((status, err) => {
      setGhostStatus(status);
      setGhostError(err);
    });
  }, []);

  const isFetching = ghostStatus === "fetching";
  const hasError = ghostStatus === "error";

  const title = !enabled
    ? "AI autocomplete: off (click to enable)"
    : hasError
      ? `Autocomplete error: ${ghostError ?? "unknown"}`
      : isFetching
        ? "Fetching completion..."
        : "AI autocomplete: on (click to disable)";

  return (
    <button
      onClick={() => toggle(!enabled)}
      className={cn(
        "flex items-center gap-1 rounded-[var(--radius-sm)] px-1.5 py-0.5 -my-0.5",
        "transition-colors duration-100",
        !enabled
          ? "text-[var(--text-muted)] opacity-50 hover:bg-[var(--bg-surface-hover)] hover:opacity-80"
          : hasError
            ? "text-[var(--color-soft-red)] hover:bg-[var(--color-soft-red)]/10"
            : "text-[var(--color-lever-orange)] hover:bg-[var(--color-lever-orange)]/10",
      )}
      title={title}
    >
      {isFetching ? (
        <CircleNotch size={10} weight="bold" className="animate-spin" />
      ) : hasError ? (
        <WarningCircle size={10} weight="fill" />
      ) : (
        <Sparkle size={10} weight={enabled ? "fill" : "regular"} />
      )}
      <span className="text-[10px]">
        {!enabled ? "Autocomplete" : hasError ? "Autocomplete error" : "Autocomplete"}
      </span>
    </button>
  );
}

function VitalsIndicator() {
  const metrics = useVitalsStore((s) => s.metrics);
  if (!metrics) return null;

  const readability = metrics.readabilityScore;
  const color = readability >= 50 ? "var(--vitals-green)" : readability >= 30 ? "var(--vitals-amber)" : "var(--vitals-red)";

  return (
    <span className="flex items-center gap-1.5 tabular-nums">
      <span
        className="inline-block h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span>R: {readability}</span>
    </span>
  );
}

export function StatusBar({ content, docType, className }: StatusBarProps) {
  const cursorLine = useEditorStore((s) => s.cursorLine);
  const editorView = useEditorStore((s) => s.editorView);

  const cursorCol = useMemo(() => {
    if (!editorView) return 1;
    const { main } = editorView.state.selection;
    const line = editorView.state.doc.lineAt(main.head);
    return main.head - line.from + 1;
  }, [editorView]);

  const wordCount = useMemo(() => countWords(content, docType), [content, docType]);
  const charCount = content.length;

  return (
    <div
      className={cn(
        "flex items-center justify-between px-3 py-1 shrink-0 min-w-0 overflow-hidden",
        "border-t border-[var(--border-hairline)] bg-[var(--bg-surface)]",
        "text-[10px] text-[var(--text-muted)] select-none",
        className,
      )}
    >
      <div className="flex items-center gap-3 min-w-0 overflow-hidden">
        <span className="tabular-nums whitespace-nowrap">
          Ln {cursorLine}, Col {cursorCol}
        </span>
        <span className="h-2.5 w-px bg-[var(--border-hairline)] shrink-0" />
        <span className="tabular-nums whitespace-nowrap">
          {wordCount.toLocaleString()} {wordCount === 1 ? "word" : "words"}
        </span>
        <span className="h-2.5 w-px bg-[var(--border-hairline)] shrink-0 hidden sm:block" />
        <span className="tabular-nums whitespace-nowrap hidden sm:inline">
          {charCount.toLocaleString()} {charCount === 1 ? "char" : "chars"}
        </span>
      </div>
      <div className="flex items-center gap-3 shrink-0 whitespace-nowrap">
        <span className="hidden md:flex items-center gap-3">
          <VitalsIndicator />
          <span className="h-2.5 w-px bg-[var(--border-hairline)]" />
        </span>
        <span className="hidden lg:flex items-center gap-3">
          <CompletionIndicator />
          <span className="h-2.5 w-px bg-[var(--border-hairline)]" />
        </span>
        <span className="uppercase tracking-wider font-medium">
          {docType === "latex" ? "LaTeX" : docType === "markdown" ? "Markdown" : "Document"}
        </span>
        <span className="hidden sm:flex items-center gap-3">
          <span className="h-2.5 w-px bg-[var(--border-hairline)]" />
          <span>UTF-8</span>
        </span>
      </div>
    </div>
  );
}
